let recentlyViewedProducts = getRecentlyViewedProductsCookieData();

document.addEventListener('recently-viewed-add', (event) => {
  addProduct(event.detail.handle);
});

/**
 * Event listener for 'recently-viewed' event.
 * - Re-checks the cookie for recently viewed products changes.
 * - Calls `addBadges` to update the product cards with the recently viewed
 *   products.
 */
document.addEventListener('recently-viewed', () => {
  recheckCookie();
  if (window.recentlyViewedStrings?.show) {
    addBadges(recentlyViewedProducts);
  }
});

window.addEventListener('load', () => {
  if (window.recentlyViewedStrings?.show) {
    addBadges(recentlyViewedProducts);
  }
  handleNavigationLinks();
});

/**
 * Retrieves recently viewed product handles from the cookie.
 * - Falls back to an empty array if the cookie is not set or invalid.
 * @returns {Array} - An array of recently viewed product handles.
 */
function getRecentlyViewedProductsCookieData() {
  const cookieValue = cookies.getCookie('recentlyViewedProducts');
  const fallbackValue = [];

  if (!cookieValue) {
    return fallbackValue;
  }

  try {
    const data = JSON.parse(cookieValue);
    return data || fallbackValue;
  } catch {
    return fallbackValue;
  }
}
/**
 * Re-checks the cookie for recently viewed products.
 * - Updates the `recentlyViewedProducts` variable with the latest data from the
 *   cookie.
 */
function recheckCookie() {
  recentlyViewedProducts = getRecentlyViewedProductsCookieData();
}

function addBadges(productHandles) {
  productHandles.forEach((productHandle) => {
    const element = document.querySelector(
      `a[data-handle="${productHandle}"]:not(.recently-viewed-product):not(.tagalys-product):not([data-app-exclusive])`,
    );
    if (element) {
      clearExistingBadges(element);
      addRecentlyViewedBadge(element);
    }
  });
}

function clearExistingBadges(element) {
  const badges = element.querySelectorAll('.badge');
  badges.forEach((badge) => badge.remove());
}

function addRecentlyViewedBadge(element) {
  const badge = document.createElement('div');
  badge.textContent = window.recentlyViewedStrings.badge || 'Recently Viewed';
  badge.classList.add(
    'recently-viewed-label',
    'safari-flicker-fix',
    'absolute',
    'top-[15px]',
    'left-[15px]',
    'text-darker-gray',
    'text-[10px]',
    'leading-4',
    'z-10',
    'capitalize',
    'view-3-hidden',
  );
  element.querySelector('.relative').appendChild(badge);
}

/**
 * Add the recently viewed count to the navigation links, or hide the links
 * if there are no recently viewed products.
 * - Finds all the links in the mobile and desktop menus that point to the
 *   recently viewed page.
 * - Fetches the product count from the Search API.
 * - Updates the links with the product count.
 */
async function handleNavigationLinks() {
  const recentlyViewedPageUrl = window.recentlyViewedStrings?.pageUrl;
  if (!recentlyViewedPageUrl) return;

  const recentlyViewedPageLinks = document.querySelectorAll(
    `desktop-menu a[href="${recentlyViewedPageUrl}"], mobile-menu a[href="${recentlyViewedPageUrl}"]`,
  );

  if (!recentlyViewedPageLinks.length) return;

  // No recently viewed products stored in the cookie.
  if (recentlyViewedProducts.length === 0) {
    if (window.recentlyViewedStrings?.hidePageLinksIfEmpty) {
      recentlyViewedPageLinks.forEach((link) => {
        link.classList.add('hidden');
      });
    }
    return;
  }

  const query = recentlyViewedProducts
    .map((handle) => `handle:"${handle}"`)
    .join(' OR ');

  const queryParams = new URLSearchParams({
    q: query,
    type: 'product',
    section_id: 'recently-viewed-search-count',
  });

  const searchUrl = `${window.Shopify.routes.root}search?${queryParams.toString()}`;
  const response = await fetch(searchUrl);
  if (!response.ok) {
    console.error('Failed to retrieve products from Search API');
    return;
  }
  const responseText = await response.text();
  const parsedHtml = new DOMParser().parseFromString(responseText, 'text/html');
  const productCountHTML = parsedHtml.querySelector('.shopify-section')?.innerHTML;
  const productCountNumber = parseInt(productCountHTML, 10);

  /**
   * If the API call returns 0 products, we early return to prevent creating the
   * count element on the navigation links.
   * - If the `hidePageLinksIfEmpty` flag is set, we hide the links.
   */
  if (!productCountHTML || productCountNumber === 0) {
    if (window.recentlyViewedStrings?.hidePageLinksIfEmpty) {
      recentlyViewedPageLinks.forEach((link) => {
        link.classList.add('hidden');
      });
    }
    return;
  };

  const countElement = createCountElement(productCountHTML);
  recentlyViewedPageLinks.forEach((link) => {
    const childElement = link.querySelector('span');
    if (!childElement) {
      return;
    }

    const existingCount = link.querySelector('.recently-viewed-count');
    if (existingCount) {
      existingCount.remove();
    }
    childElement.appendChild(countElement.cloneNode(true));
  });
}
/**
 * Build the product count span element.
 * @param {String} productCountHTML - The product count HTML string.
 * @returns {HTMLElement} The span element containing the product count.
 */
function createCountElement(productCountHTML) {
  const countElement = document.createElement('span');
  countElement.innerText = productCountHTML;
  countElement.classList.add('recently-viewed-count', 'text-[8px]', 'leading-4', '-translate-x-[1px]', '-translate-y-[6px]');
  return countElement;
}

/**
 * Adds a product handle to the recently viewed products list.
 * - Rechecks the cookie for any changes to ensure the list is up to date.
 * - Checks if the new handle is in the list already.
 * - If not, adds it to the beginning of the list.
 * - If it is, moves it to the beginning of the list.
 * - Limits the list to a maximum number of products defined in the config.
 * - Updates the cookie with the new list.
 * @param {*} productHandle
 */
function addProduct(productHandle) {
  // Re-check the cookie for any changes.
  recheckCookie();

  let newRecentlyViewedProducts = [...recentlyViewedProducts];
  const productIndex = newRecentlyViewedProducts.indexOf(productHandle);
  if (productIndex === -1) {
    newRecentlyViewedProducts.unshift(productHandle);
  } else {
    newRecentlyViewedProducts.splice(productIndex, 1);
    newRecentlyViewedProducts.unshift(productHandle);
  }

  newRecentlyViewedProducts = newRecentlyViewedProducts.slice(0, window.recentlyViewedStrings.stored);

  cookies.setCookie(
    'recentlyViewedProducts',
    JSON.stringify(newRecentlyViewedProducts),
    window.recentlyViewedStrings.expires,
    window.recentlyViewedStrings.domain,
  );
}

/**
 * Updates the search parameters of a product card URL.
 * - Allows us to clear the existing parameters added via Shopify search and add
 *   new ones to help with tracking recently viewed products.
 * - Clears existing parameters if specified.
 * - Adds new parameters to the URL.
 * - Updates the product card link with the new URL.
 * @param {HTMLElement} productCard - Product card element to update.
 * @param {Object} params - Parameters to update in the product card URL.
 * @param {boolean} clearExistingParams - Whether to clear existing URL parameters.
 */
function updateProductCardSearchParams(productCard, params = {}, clearExistingParams = false) {
  const productCardLink = productCard.querySelector('a');
  if (!productCardLink) return;

  const productCardUrl = productCardLink.getAttribute('href');
  const url = new URL(productCardUrl, window.location.origin);

  if (clearExistingParams) {
    const existingParams = Array.from(url.searchParams.keys());
    existingParams.forEach((param) => {
      url.searchParams.delete(param);
    });
  }

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  productCardLink.setAttribute('href', url.toString());
}

class RecentlyViewed extends HTMLElement {
  constructor() {
    super();
  }

  async connectedCallback() {
    this.currentHandle = this.getAttribute('data-current-handle');

    if (recentlyViewedProducts.length > 0) {
      try {
        const htmlContent = await this.fetchProductsHtml(
          recentlyViewedProducts,
          this.getAttribute('section-id'),
          this.getAttribute('selector'),
        );
        this.renderProducts(htmlContent);
      } catch (error) {
        console.error('Error fetching products:', error);
      }

      this.classList.remove('!hidden');
    }
  }

  fetchProductsHtml(products, sectionId, selector) {
    const query = products.map((handle) => `handle:"${handle}"`).join(' OR ');

    const queryParams = new URLSearchParams({
      q: query,
      type: 'product',
      section_id: sectionId,
    });

    const searchUrl = `${window.Shopify.routes.root}search?${queryParams.toString()}`;

    return fetch(searchUrl)
      .then((response) => {
        if (!response.ok) throw new Error('Failed to retrieve products from Search API');
        return response.text();
      })
      .then((responseText) => {
        const parsedHtml = new DOMParser().parseFromString(responseText, 'text/html');
        const section = parsedHtml.querySelector(selector);
        if (!section) throw new Error('Failed to find the section in the response');
        return section.innerHTML;
      });
  }

  renderProducts(htmlContent) {
    const container = this.querySelector('[recently-viewed]');

    const parsedHtml = new DOMParser().parseFromString(htmlContent, 'text/html');
    const productCards = parsedHtml.querySelectorAll('li[data-handle]');

    const productMap = new Map();

    productCards.forEach((el) => {
      productMap.set(el.getAttribute('data-handle'), el);
    });

    const fragment = document.createDocumentFragment();

    /**
     * Process array of recently viewed products to display.
     * - Filters out the current product so that it doesn't show up in the list.
     * - Limits the number of products to display based on the config.
     */
    const limitAttr = parseInt(this.getAttribute('data-limit'));
    const limit = !isNaN(limitAttr) ? limitAttr : window.recentlyViewedStrings.limit;
    const handlesToRender = recentlyViewedProducts
      .filter((handle) => handle !== this.currentHandle)
      .slice(0, limit);

    handlesToRender.forEach((handle) => {
      const productEl = productMap.get(handle);
      if (productEl) {
        updateProductCardSearchParams(
          productEl,
          {
            'recently_viewed_source': 'carousel',
          },
          true,
        );

        fragment.appendChild(productEl)
      };
    });

    container.innerHTML = '';
    container.appendChild(fragment);
  }
}

customElements.define('recently-viewed', RecentlyViewed);

class RecentlyViewedPage extends HTMLElement {
  constructor() {
    super();
    this.totalProducts = 0;
    this.sectionsToRender = [
      'recently-viewed-search-grid',
      'recently-viewed-search-count',
    ];
  }

  async connectedCallback() {
    this.totalProducts = 0;

    if (recentlyViewedProducts.length > 0) {
      try {
        const htmlObject = await this.fetchHTML(
          recentlyViewedProducts,
        );

        const { 'recently-viewed-search-grid': productContent, 'recently-viewed-search-count': countContent } = htmlObject;

        this.renderProducts(productContent);

        this.setTotalProductsFromHtml(countContent);
        this.renderTotalProductsCount();

        if (this.totalProducts === 0) {
          this.showEmptyState();
        }

      } catch (error) {
        console.error('Error fetching products:', error);
      }

      this.classList.remove('!hidden');
    } else {
      this.showEmptyState();
    }
  }

  setTotalProductsFromHtml(countContent) {
    const countHtml = new DOMParser().parseFromString(countContent, 'text/html');
    const countElement = countHtml.querySelector('.shopify-section');
    const count = countElement?.innerHTML ? Number(countElement.innerHTML) : 0;
    this.totalProducts = count;
  }

  async fetchHTML(products) {
    const query = products.map((handle) => `handle:"${handle}"`).join(' OR ');

    const queryParams = new URLSearchParams({
      q: query,
      type: 'product',
      sections: this.sectionsToRender.join(','),
    });

    const searchUrl = `${window.Shopify.routes.root}search?${queryParams.toString()}`;

    const response = await fetch(searchUrl)
    if (!response.ok) throw new Error('Failed to retrieve products from Search API');

    const responseJSON = await response.json();

    return responseJSON
  }

  /**
   * Renders the product cards to the page.
   * - Parses the HTML content and extracts the product cards.
   * - Reorders the product cards based on the recently viewed products order.
   * - Appends the reordered product cards to the container.
   * @param {String} htmlContent - The product card HTML content to be rendered.
   * @returns
   */
  renderProducts(htmlContent) {
    if (!htmlContent) return;

    const parsedHtml = new DOMParser().parseFromString(htmlContent, 'text/html');
    const container = this.querySelector('[recently-viewed]');

    const productsList = parsedHtml.querySelector('ul');
    if (!productsList) return;

    const productMap = new Map(
      Array.from(productsList.querySelectorAll('li[data-handle]')).map((el) => [
        el.getAttribute('data-handle'),
        el,
      ])
    );

    const fragment = document.createDocumentFragment();
    recentlyViewedProducts.forEach((handle) => {
      const productEl = productMap.get(handle);
      if (productEl) {
        updateProductCardSearchParams(
          productEl,
          {
            'recently_viewed_source': 'page',
          },
          true,
        );
        fragment.appendChild(productEl);
      }
    });

    productsList.innerHTML = '';
    productsList.appendChild(fragment);
    container.innerHTML = productsList.outerHTML;
  }

  renderTotalProductsCount() {
    const totalProductsContainer = this.querySelector('[data-item-count-wrapper]');
    if (!totalProductsContainer) return;

    const totalProductsElement = this.querySelector('[data-item-count]');
    if (totalProductsElement) {
      totalProductsElement.textContent = this.totalProducts || '0';
    }

    totalProductsContainer.classList.remove('hidden');
  }

  showEmptyState() {
    const emptyStateContainer = this.querySelector('[data-empty-state]');
    if (emptyStateContainer) {
      emptyStateContainer.classList.remove('hidden');
    }

    const productContainer = this.querySelector('[recently-viewed]');
    if (productContainer) {
      productContainer.classList.add('hidden');
    }
  }
}

customElements.define('recently-viewed-page', RecentlyViewedPage);